import { getBackendUrl } from '../utils/apiUrl';
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

import devLog from '../utils/devLog';

const BACKEND_URL = getBackendUrl();

/**
 * Get session token for authenticated API requests
 * MEMORY BUG FIX: Must send Bearer token for cross-origin requests
 */
import { getSessionToken as getToken } from '../utils/secureStorage';
const getSessionToken = () => getToken() || '';

export const UserMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [clearingMemory, setClearingMemory] = useState(false);
  const [showClearModal, setShowClearModal] = useState(false);
  const [memoryCount, setMemoryCount] = useState(0);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleClearMemoryClick = async () => {
    devLog.log('🔄 [UserMenu] SYNCHRONIZATION - Clear Memory button clicked');
    setIsOpen(false); // Close the menu
    
    try {
      // First, check if user has any memories stored
      devLog.log('🔄 [UserMenu] SYNCHRONIZATION - Fetching memory status from /api/memory/status...');
      
      // MEMORY BUG FIX: Include Authorization header with Bearer token
      const sessionToken = getSessionToken();
      const headers = {
        'Content-Type': 'application/json'
      };
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }
      
      const statusResponse = await axios.get(`${BACKEND_URL}/api/memory/status`, {
        withCredentials: true,
        headers: headers
      });
      devLog.log('🔄 [UserMenu] SYNCHRONIZATION - Memory status response:', statusResponse.data);
      devLog.log('🔄 [UserMenu] SYNCHRONIZATION - Count:', statusResponse.data.count);
      
      const count = statusResponse.data.count || 0;
      setMemoryCount(count);
      
      if (count === 0) {
        // Show info message with 0 explicitly
        alert('ℹ️ 0 AI memories stored at the moment.\n\nThe system will start remembering your projects once you generate your first prompt.');
        return;
      }
      
      // Show the modal
      devLog.log('🔄 [UserMenu] SYNCHRONIZATION - Showing Clear Memory modal with count:', count);
      setShowClearModal(true);
      
    } catch (statusError) {
      console.error('[UserMenu] Memory status check failed:', statusError);
      alert(`⚠️ Unable to check memory status.\n\nError: ${statusError.response?.data?.detail || statusError.message}\n\nPlease try again or contact support if the issue persists.`);
    }
  };

  const handleConfirmClear = async () => {
    devLog.log('[UserMenu] Confirm clear clicked');
    setClearingMemory(true);
    
    try {
      // MEMORY BUG FIX: Include Authorization header with Bearer token
      const sessionToken = getSessionToken();
      const headers = {
        'Content-Type': 'application/json'
      };
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }
      
      // Proceed with clearing
      devLog.log('[UserMenu] Sending clear request...');
      const response = await axios.delete(`${BACKEND_URL}/api/memory/clear`, {
        withCredentials: true,
        headers: headers
      });

      devLog.log('[UserMenu] Clear response:', response.data);

      if (response.data.status === 'success') {
        const deletedCount = response.data.deleted_count || 0;
        setShowClearModal(false);
        setMemoryCount(0); // SYNCHRONIZATION FIX: Update local state immediately
        
        // Show success toast
        showSuccessToast(`✅ Memory cleared! ${deletedCount} project(s) forgotten.`);
        
        // SYNCHRONIZATION FIX: Trigger page reload after 2 seconds to refresh Expert Council banner
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        alert('❌ Failed to clear memory. Please try again.');
      }
    } catch (error) {
      console.error('[UserMenu] Failed to clear memory:', error);
      
      // More specific error message
      if (error.response?.status === 401) {
        alert('❌ Authentication required.\n\nYour session may have expired. Please sign out and sign in again.');
      } else if (error.response?.status === 500) {
        alert(`❌ Server error while clearing memory.\n\nError: ${error.response?.data?.detail || 'Unknown error'}\n\nPlease try again later.`);
      } else {
        alert(`❌ Failed to clear memory.\n\nError: ${error.response?.data?.detail || error.message}\n\nPlease try again.`);
      }
    } finally {
      setClearingMemory(false);
    }
  };
  
  const handleCancelClear = () => {
    devLog.log('[UserMenu] Cancel clear clicked');
    setShowClearModal(false);
  };
  
  const showSuccessToast = (message) => {
    const toast = document.createElement('div');
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: #F4F4F4;
      color: #1F2937;
      padding: 16px 24px;
      border-radius: 8px;
      font-weight: 600;
      box-shadow: 0 10px 25px rgba(0,0,0,0.2);
      z-index: 10001;
      animation: slideInFromTop 0.3s ease-out;
      white-space: nowrap;
      text-align: center;
    `;
    toast.textContent = message;
    
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideInFromTop {
        from { transform: translate(-50%, -100px); opacity: 0; }
        to { transform: translate(-50%, 0); opacity: 1; }
      }
      @keyframes slideOutToTop {
        from { transform: translate(-50%, 0); opacity: 1; }
        to { transform: translate(-50%, -100px); opacity: 0; }
      }
    `;
    document.head.appendChild(style);
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.style.animation = 'slideOutToTop 0.3s ease-in';
      setTimeout(() => {
        if (toast.parentNode) document.body.removeChild(toast);
        if (style.parentNode) document.head.removeChild(style);
      }, 300);
    }, 3000);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Mobile-responsive dropdown positioning
  useEffect(() => {
    // Inject mobile styles for the dropdown
    const styleId = 'user-menu-mobile-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @media (max-width: 640px) {
          #user-menu-dropdown {
            position: fixed !important;
            left: 16px !important;
            right: 16px !important;
            top: 60px !important;
            width: auto !important;
            max-width: calc(100vw - 32px) !important;
          }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  if (!user) return null;

  return (
    <div className="relative" ref={menuRef}>
      {/* DEPLOYMENT FIX 1️⃣: Profile button with explicit cursor-pointer */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-1 sm:space-x-2 px-1.5 sm:px-3 py-1.5 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors max-w-[140px] sm:max-w-none cursor-pointer"
      >
        <span className="text-[10px] sm:text-sm font-medium text-gray-700 truncate block max-w-[100px] sm:max-w-none">{user.name || user.email}</span>
        <svg className={`w-3 h-3 sm:w-4 sm:h-4 text-gray-600 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <>
          {/* Mobile-responsive dropdown menu */}
          <div 
            className="absolute mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 right-0 w-56 sm:right-0 sm:w-56"
            style={{
              // Mobile override - applied via media query in useEffect
            }}
            id="user-menu-dropdown"
          >
          <button
            onClick={() => {
              navigate('/account');
              setIsOpen(false);
            }}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
          >
            <span>👤</span>
            <span>My Account</span>
          </button>
          
          <button
            onClick={() => {
              navigate('/settings');
              setIsOpen(false);
            }}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
          >
            <span>⚙️</span>
            <span>Settings</span>
          </button>
          
          {/* V37.44.9 — "Purchase Credits" menu item removed (free-access mode). */}

          {/* V37.44.38 — "Project Prompts" / "Bug Fix Prompts" entries
              relocated to the top-nav center (see Header.jsx). Separator
              + buttons removed from this dropdown to avoid duplication. */}

          <button
            onClick={handleClearMemoryClick}
            disabled={clearingMemory}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>🗑️</span>
            <span>{clearingMemory ? 'Clearing...' : 'Clear AI Memory'}</span>
          </button>
          
          <div className="border-t border-gray-200 my-1"></div>
          
          <button
            onClick={() => {
              logout();
              setIsOpen(false);
            }}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
          >
            <span>🚪</span>
            <span>Logout</span>
          </button>
          
        </div>
        </>
      )}
      
      {/* Clear Memory Confirmation Modal */}
      {showClearModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            paddingTop: '20px',
            zIndex: 9999,
            animation: 'fadeIn 0.2s ease-out'
          }}
          onClick={handleCancelClear}
        >
          <div 
            style={{
              backgroundColor: '#F4F4F4',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '450px',
              width: '90%',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)',
              animation: 'slideInFromTop 0.3s ease-out',
              position: 'relative'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: '#1F2937',
              marginBottom: '16px',
              textAlign: 'center'
            }}>
              Clear AI Memory
            </h3>
            
            <p style={{
              fontSize: '15px',
              color: '#4B5563',
              lineHeight: '1.6',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              You currently have <strong>{memoryCount}</strong> saved project {memoryCount === 1 ? 'memory' : 'memories'}.
              <br /><br />
              Clearing will permanently delete them. Proceed?
            </p>
            
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center'
            }}>
              <button
                onClick={handleCancelClear}
                disabled={clearingMemory}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#FFFFFF',
                  color: '#374151',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: clearingMemory ? 'not-allowed' : 'pointer',
                  opacity: clearingMemory ? 0.5 : 1,
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => !clearingMemory && (e.target.style.backgroundColor = '#F9FAFB')}
                onMouseOut={(e) => (e.target.style.backgroundColor = '#FFFFFF')}
              >
                Cancel
              </button>
              
              <button
                onClick={handleConfirmClear}
                disabled={clearingMemory}
                style={{
                  padding: '10px 24px',
                  backgroundColor: '#000000',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: clearingMemory ? 'not-allowed' : 'pointer',
                  opacity: clearingMemory ? 0.5 : 1,
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => !clearingMemory && (e.target.style.backgroundColor = '#1F2937')}
                onMouseOut={(e) => (e.target.style.backgroundColor = '#000000')}
              >
                {clearingMemory ? 'Clearing...' : 'OK'}
              </button>
            </div>
          </div>
          
          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes slideInFromTop {
              from { 
                transform: translateY(-30px);
                opacity: 0;
              }
              to { 
                transform: translateY(0);
                opacity: 1;
              }
            }
          `}</style>
        </div>
      )}
    </div>
  );
};
